import { useEffect, useRef, useState } from "react";
import { navigate } from "../../app/router.js";
import { useApi, useSessions } from "../../app/services.js";
import { errorMessage } from "../../lib/useResource.js";
import { Button, Eyebrow, Spinner } from "../../ui/index.js";
import { AuthLayout } from "./AuthLayout.js";

interface TokenScreenProps {
  kind: "login" | "invite";
  token: string;
}

/** Trades a sign-in or invite token for a session. Each token works once, so it is sent once. */
export function TokenScreen({ kind, token }: TokenScreenProps) {
  const api = useApi();
  const sessions = useSessions();
  const [error, setError] = useState<string | null>(null);
  const sent = useRef<string | null>(null);

  useEffect(() => {
    if (sent.current === token) return;
    sent.current = token;
    const routeId = kind === "login" ? "authExchange" : "authInvite";
    api
      .call(routeId, { body: { token } })
      .then((session) => {
        sessions.set(session);
        navigate(session.active === null ? "/start" : "/activity", { replace: true });
      })
      .catch((failure: unknown) => { setError(errorMessage(failure)); });
  }, [api, sessions, kind, token]);

  if (error !== null) {
    return (
      <AuthLayout>
        <Eyebrow>{kind === "login" ? "Sign-in link" : "Invite"}</Eyebrow>
        <h1 className="headline">That link did not work.</h1>
        <p className="auth-lede">{error}</p>
        <Button variant="primary" onClick={() => { navigate("/login", { replace: true }); }}>
          Get a new sign-in link
        </Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <Eyebrow>{kind === "login" ? "Signing you in" : "Joining"}</Eyebrow>
      <div className="auth-wait">
        <Spinner size={22} />
      </div>
    </AuthLayout>
  );
}
